/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import customTable from '../../../../share/components/vue/PaginationTable';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import util from '../../../../share/util/utils';
import error from '../../../../common/nodejs/errors';
const moment = require('moment');

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  NODATA: 'nodata',
  LIST: 'list',
  FAILED: 'failed'
};

/** Read API 実行フラグ */
const API_FLAG = {
  INIT: 'init',           // 初期処理
  LASTED: 'latest',       // 最新
  ONE_PREV: 'one_prev',   // 1つ前
  TWO_PREV: 'two_prev',   // 2つ前
  THR_PREV: 'thr_prev',   // 3つ前
  FOUR_PREV: 'four_prev'  // 4つ前
};

/** DID定数定義 */
const DID_CONST = {
  // IG ON回数
  DID101E: 4126,
  DID10A7: 4263,
  DID10A8: 4264,
  DID10A9: 4265,
  DID10AA: 4266,
  DID10AB: 4267,
  // 絶対時間
  DID1014: 4116,
  DID10BA: 4282,
  DID10BB: 4283,
  DID10BC: 4284,
  DID10BD: 4285,
  DID10BE: 4286,
};

// 絶対時間 記録なし変換対象値
const NO_DATA_FOR_ABSOLUTE_TIME = '2000-00-00T00:00:00Z';

export default {
  name: 'requirement052',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      tableColumns: [],
      items: [],
      selectedRow: {},
      currentCnt: {},
      absoluteTime: null,
      apiFlag: API_FLAG.INIT,
      systemId: '',
      isSaveButton: false,
      modal_requirement_052: false
    };
  },
  components: {
    'custom-table': customTable
  },
  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.START:
        case PHASE.LIST:
        case PHASE.NODATA:
        default:
          return 'secondary';
      }
    }
  },
  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_052 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** モード遷移 OKボタン押下 */
    async readProcess() {
      await this.read();
    },

    /** OKボタン押下 */
    async exitProcess() {
      // Wizardを閉じる
      this.closeWizard();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** データ読み出し */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        let reqBody = {};
        if (this.apiFlag === API_FLAG.INIT) {
          reqBody = { id_token: token.id_token, type: 'standby' };
        } else {
          reqBody = { id_token: token.id_token, type: 'start', flag: this.apiFlag };
        }

        // データ読み出し
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          reqBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // flag値によって処理分岐（画面表示orファイル出力）
              if (this.apiFlag === API_FLAG.INIT) {
                this.readResultDisplay(response.data.user_data);
              } else {
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                if (this.readResultOutputFile(response.data.user_data)) {
                  // ファイル出力正常終了時、画面を閉じる
                  this.closeWizard();
                }
              }
              break;
            case config.STATUS_WSP_FAILED:
              switch (response.data.async_code) {
                case error.WSP_052_NO_DATA:
                  // 全ての読出し値が0
                  this.phase = PHASE.NODATA;
                  break;
                default:
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * read API Result処理1 一覧画面表示
     * @param {string} response レスポンスデータ
     */
    readResultDisplay(response) {
      this.phase = PHASE.LIST;
      setTimeout(() => {
        this.tableDataConvert(response);
        this.settableColumns();
      });
    },

    /** 
     * read API Result処理2 ファイル出力
     * @param {string} response レスポンスデータ
     */
    readResultOutputFile(response) {
      try {
        const fName = moment().format("YYYYMMDDHHmmss");
        const rd = response.value.match(/.{2}/g);
        const buffer = new ArrayBuffer(rd.length);
        const dataview = new DataView(buffer);
        let offset = 0;
        for (const element of rd) {
          const hex16 = parseInt(element, 16);
          dataview.setUint8(offset, hex16, false);
          offset += 1;
        }
        const result = new Uint8Array(buffer);

        const blob = new Blob([result], { type: "application/octet-stream" });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fName + '.' + response.file_extension;
        link.click();
        link.remove();
      } catch {
        this.showUnexpectedError();
        return false;
      }
      return true;
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /* Tableヘッダ */
    settableColumns() {
      this.tableColumns = [
        { name: 'history', label: 'individual.label_table_header_history', originalSortFunc: this.rabOperationRecordSort },
        { name: 'ig_on_value', label: 'individual.label_table_header_ig_on_count', originalSortFunc: this.rabOperationRecordSort },
        { name: 'type', label: 'message.header_vehicle_dtc_table_type' },
        { name: 'absolute_time', label: 'individual.header_absolute_time', originalSortFunc: this.rabOperationRecordSort }
      ];
    },

    /**
     * データ表示用変換
     * @param {Array} response.igon_counts レスポンスデータ(IGON回数)
     * @param {Array} response.absolute_times レスポンスデータ(絶対時間)
    */
    tableDataConvert(response) {
      for (const element of response.igon_counts) {
        // 絶対時間データのオブジェクト抽出用変数 初期化
        let absoluteTimeObj = undefined;

        // IGON回数が0の場合は"記録なし"を表示
        if (element.value === 0) {
          element.ig_on_value = this.$t(`individual.label_no_record`);
        } else {
          element.ig_on_value = element.value;
        }
        // 現在値IGON回数の取得
        if (element.id === DID_CONST.DID101E) {
          this.currentCnt = element.value;
          // 現在値 絶対時間取得
          absoluteTimeObj = response.absolute_times.find((obj) => obj.id === DID_CONST.DID1014);
          if (absoluteTimeObj) {
            this.absoluteTime = this.convertLocalTime(absoluteTimeObj.value, true);
          }
          continue;
        }
        switch (element.id) {
          // 最新
          case DID_CONST.DID10A7:
            element.history = this.$t(`individual.label_table_history_latest`);
            element.flag = API_FLAG.LASTED;
            // 最新 絶対時間取得
            absoluteTimeObj = response.absolute_times.find((obj) => obj.id === DID_CONST.DID10BA);
            if (absoluteTimeObj) {
              element.absolute_time = this.convertLocalTime(absoluteTimeObj.value, false);
            }
            break;
          // 1回前
          case DID_CONST.DID10A8:
            element.history = this.$t(`individual.label_table_history_one_prev`);
            element.flag = API_FLAG.ONE_PREV;
            // 1回前 絶対時間取得
            absoluteTimeObj = response.absolute_times.find((obj) => obj.id === DID_CONST.DID10BB);
            if (absoluteTimeObj) {
              element.absolute_time = this.convertLocalTime(absoluteTimeObj.value, false);
            }
            break;
          // 2回前
          case DID_CONST.DID10A9:
            element.history = this.$t(`individual.label_table_history_two_prev`);
            element.flag = API_FLAG.TWO_PREV;
            // 2回前 絶対時間取得
            absoluteTimeObj = response.absolute_times.find((obj) => obj.id === DID_CONST.DID10BC);
            if (absoluteTimeObj) {
              element.absolute_time = this.convertLocalTime(absoluteTimeObj.value, false);
            }
            break;
          // 3回前
          case DID_CONST.DID10AA:
            element.history = this.$t(`individual.label_table_history_thr_prev`);
            element.flag = API_FLAG.THR_PREV;
            // 3回前 絶対時間取得
            absoluteTimeObj = response.absolute_times.find((obj) => obj.id === DID_CONST.DID10BD);
            if (absoluteTimeObj) {
              element.absolute_time = this.convertLocalTime(absoluteTimeObj.value, false);
            }
            break;
          // 4回前
          case DID_CONST.DID10AB:
            element.history = this.$t(`individual.label_table_history_four_prev`);
            element.flag = API_FLAG.FOUR_PREV;
            // 4回前 絶対時間取得
            absoluteTimeObj = response.absolute_times.find((obj) => obj.id === DID_CONST.DID10BE);
            if (absoluteTimeObj) {
              element.absolute_time = this.convertLocalTime(absoluteTimeObj.value, false);
            }
            break;
        }
        element.type = this.$t(`message.status_vehicle_dtc_type_common`);
        this.items = this.items.concat(element);
      }
    },

    /**
     * 日時変換処理
     * @param {String} value UTC時間文字列
     * @param {Boolean} currentFlg true: 現在値, false: 履歴データ
    */
    convertLocalTime(value, currentFlg) {
      // 日付に解釈可能な文字列かを判定
      if (moment(value).isValid()) {
        // 解釈可能な場合はLocalTimeに変換し表示
        return moment(value).format("YYYY/MM/DD HH:mm:ss");
      } else {
        // 解釈不可能の場合
        if (value === NO_DATA_FOR_ABSOLUTE_TIME && !currentFlg) {
          // 0値に相当する値の場合、'記録なし'を表示
          return this.$t(`individual.label_no_record`);
        } else {
          // 上記以外の場合はそのまま表示
          return value;
        }
      }
    },

    /** Table 列クリックイベント */
    rowClick(row) {
      this.selectedRow = row;
      this.apiFlag = row.flag;
      this.setHighLight();
      this.switchSaveButton();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.items.forEach(item => {
        if (item === this.selectedRow) {
          item.fieldSpec = {};
          item.fieldSpec.history = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.ig_on_value = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.type = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.absolute_time = config.LIST_ITEM_HIGHLIGHT;
        } else {
          if (item.fieldSpec) {
            delete item.fieldSpec;
          }
        }
      });
    },

    /**
     * 保存ボタンの活性非活性を制御
     */
    switchSaveButton() {
      if (this.selectedRow.value === 0 || this.selectedRow.value === '-') {
        // IGON回数が 0(記録なし) または - の場合
        this.isSaveButton = false;
      } else {
        this.isSaveButton = true;
      }
    },

    // RAB作動履歴 独自ソート(履歴)
    rabOperationRecordSort(param) {
      if (!param.data) {
        return null;
      }
      // 結果リスト
      let resultItems = [];
      // ソート可能値リスト
      let normalSortList = [];
      // その他特殊ソート対象リスト
      const otherList = [];
      // hyphen値リスト
      const hyphenList = [];
      param.data.forEach(element => {
        if (param.sortKey === 'history') {
          if (element[param.sortKey].indexOf(this.$t(`individual.label_table_history_latest`)) > -1) {
            otherList.push(element);
          } else {
            normalSortList.push(element);
          }
        } else if (param.sortKey === 'ig_on_value' || param.sortKey === 'absolute_time') {
          if (element[param.sortKey] === this.$t(`individual.label_no_record`)) {
            otherList.push(element);
          } else if (element[param.sortKey] === this.$t(`message.common_hyphen`)) {
            hyphenList.push(element);
          } else {
            normalSortList.push(element);
          }
        }
      });
      normalSortList = util.sortItems(normalSortList, param.sortKey, param.sortDir);

      // 昇順の場合
      if (param.sortDir === config.ASC) {
        if (param.sortKey === 'history') {
          resultItems = otherList.concat(normalSortList);
        } else if (param.sortKey === 'ig_on_value' || param.sortKey === 'absolute_time') {
          resultItems = normalSortList.concat(otherList).concat(hyphenList);
        }
        return resultItems;
      }
      // 降順の場合
      else {
        if (param.sortKey === 'history') {
          resultItems = normalSortList.concat(otherList);
        } else if (param.sortKey === 'ig_on_value' || param.sortKey === 'absolute_time') {
          resultItems = normalSortList.concat(otherList).concat(hyphenList);
        }
        return resultItems;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_052 = false;
    }
  },

  async mounted() {
    this.init();
    // 実行処理
    await this.read();
  }
};
